import React, { cloneElement, useContext, useState } from 'react'

import { useTranslation } from 'react-i18next'

import Button from '@components/shared/Button'
import Dialog from '@components/shared/Dialog'
import Dropdown from '@components/shared/form/Dropdown'
import Slider from '@components/shared/form/Slider'
import { TitleContext } from '@providers/TitleProvider'
import {
  createAssignment,
  DEFAULT_ATTEMPTS,
  DEFAULT_DIFFICULTY,
  DEFAULT_SCORE,
} from '@services/SoundCheck'
import { useDialogStore } from '@stores/dialog.store'

const SoundCheckDialog: React.FC = () => {
  const { t } = useTranslation()
  const closeDialog = useDialogStore((s) => s.closeDialog)

  const { title } = useContext(TitleContext)

  const [difficulty, setDifficulty] = useState(DEFAULT_DIFFICULTY)
  const [minimumScore, setMinimumScore] = useState(DEFAULT_SCORE)
  const [maxAttempts, setMaxAttempts] = useState(DEFAULT_ATTEMPTS)

  const create = async (): Promise<void> => {
    if (!title.soundcheck_exercise_id) return
    const assignmentId = await createAssignment({
      exerciseId: title.soundcheck_exercise_id,
      difficulty,
      minimumScore,
      maxAttempts,
    })
    window.location.href = `/soundcheck/${assignmentId}`
  }

  return (
    <Dialog name="sound_check" title={t('action.createSoundCheckScore')} maxWidth={400}>
      <Setting name="scoreGoal">
        <div className="flex items-center">
          <span>{minimumScore}%</span>
          <Slider
            className="w-[110px]"
            min={0}
            max={100}
            step={5}
            onChange={setMinimumScore}
            value={minimumScore}
          />
        </div>
      </Setting>
      <Setting name="difficultyLevel">
        <Dropdown
          className="w-[110px]"
          variant="sm"
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}>
          {
            <>
              <option value="easy">{t('difficulty.easy')}</option>
              <option value="medium">{t('difficulty.medium')}</option>
              <option value="hard">{t('difficulty.hard')}</option>
            </>
          }
        </Dropdown>
      </Setting>
      <Setting name="attemptsAllowed">
        <Dropdown
          className="w-[110px]"
          variant="sm"
          value={maxAttempts}
          onChange={(e) => setMaxAttempts(e.target.value)}>
          {[
            <option key="unlimited" value="Unlimited">
              {t('soundcheck.unlimited')}
            </option>,
            ...Array(9)
              .fill(1)
              .map((n, i) => (
                <option key={i} value={i + 1}>
                  {i + 1}
                </option>
              )),
          ]}
        </Dropdown>
      </Setting>

      <div className="mt-[2rem] flex flex-row justify-end gap-1">
        <Button variant="secondary" onClick={() => closeDialog('sound_check')}>
          {t('action.cancel')}
        </Button>
        <Button variant="primary" onClick={create}>
          {t('action.create')}
        </Button>
      </div>
    </Dialog>
  )
}

interface SettingProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name: string
}

const Setting: React.FC<SettingProps> = ({ name, children }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-row justify-between items-baseline h-[45px] pb-4">
      <label htmlFor={name}>{t(`label.${name}`)}</label>
      {
        // @ts-ignore: No overload matches this call.
        cloneElement(children, { name })
      }
    </div>
  )
}

export default SoundCheckDialog
